export type AlignEdge = "left" | "center" | "right" | "top" | "middle" | "bottom"

type PlaceInformation = {
    left?: number
    top?: number
    width?: number
    height?: number
}

type LayoutField = {
    id: string
    placeInformation: PlaceInformation
}

type Bounds = {
    width: number
    height: number
}

const DEFAULT_SIZE = 32

const getBox = (field: LayoutField) => {
    const left = field.placeInformation.left || 0
    const top = field.placeInformation.top || 0
    const width = field.placeInformation.width || DEFAULT_SIZE
    const height = field.placeInformation.height || DEFAULT_SIZE
    return {left, top, width, height, right: left + width, bottom: top + height}
}

export const getMovingFieldIds = (fieldId: string, selectedFieldIds: string[]) => {
    if (selectedFieldIds.includes(fieldId) && selectedFieldIds.length > 1) {
        return selectedFieldIds
    }
    return [fieldId]
}

type SelectionRect = {
    left: number
    top: number
    width: number
    height: number
}

const normalizeRect = (rect: SelectionRect) => {
    const left = rect.width >= 0 ? rect.left : rect.left + rect.width
    const top = rect.height >= 0 ? rect.top : rect.top + rect.height
    return {
        left,
        top,
        width: Math.abs(rect.width),
        height: Math.abs(rect.height),
        right: left + Math.abs(rect.width),
        bottom: top + Math.abs(rect.height)
    }
}

export const getFieldIdsInRect = (fields: LayoutField[], rect: SelectionRect): string[] => {
    const area = normalizeRect(rect)
    if (area.width <= 0 && area.height <= 0) return []

    return fields
        .filter((field) => {
            const box = getBox(field)
            return box.left < area.right && box.right > area.left && box.top < area.bottom && box.bottom > area.top
        })
        .map((field) => field.id)
}

export const getDragBounds = (
    field: LayoutField,
    fields: LayoutField[],
    selectedFieldIds: string[],
    boundaryWidth: number,
    boundaryHeight: number
) => {
    const fallback = {top: 0, left: 0, right: boundaryWidth, bottom: boundaryHeight}
    if (!selectedFieldIds.includes(field.id) || selectedFieldIds.length < 2) {
        return fallback
    }

    const mine = getBox(field)
    let minLeft = mine.left
    let maxLeft = mine.left
    let minTop = mine.top
    let maxTop = mine.top

    for (const item of fields) {
        if (!selectedFieldIds.includes(item.id)) continue
        const box = getBox(item)
        if (box.left < minLeft) minLeft = box.left
        if (box.left > maxLeft) maxLeft = box.left
        if (box.top < minTop) minTop = box.top
        if (box.top > maxTop) maxTop = box.top
    }

    return {
        left: mine.left - minLeft,
        top: mine.top - minTop,
        right: boundaryWidth - (maxLeft - mine.left),
        bottom: boundaryHeight - (maxTop - mine.top)
    }
}

export const alignFieldPositions = <T extends LayoutField>(
    fields: T[],
    selectedFieldIds: string[],
    edge: AlignEdge,
    bounds: Bounds
): T[] => {
    const selectedIdSet = new Set(selectedFieldIds)
    const selected = fields.filter((field) => selectedIdSet.has(field.id))
    if (selected.length < 2) return fields

    const boxes = selected.map((field) => ({id: field.id, ...getBox(field)}))
    let minLeft = boxes[0].left
    let maxRight = boxes[0].right
    let minTop = boxes[0].top
    let maxBottom = boxes[0].bottom
    for (const box of boxes) {
        if (box.left < minLeft) minLeft = box.left
        if (box.right > maxRight) maxRight = box.right
        if (box.top < minTop) minTop = box.top
        if (box.bottom > maxBottom) maxBottom = box.bottom
    }
    const centerX = (minLeft + maxRight) / 2
    const centerY = (minTop + maxBottom) / 2
    const nextPositionById = new Map<string, {left: number; top: number}>()

    for (const box of boxes) {
        let left = box.left
        let top = box.top
        if (edge === "left") left = minLeft
        if (edge === "right") left = maxRight - box.width
        if (edge === "center") left = centerX - box.width / 2
        if (edge === "top") top = minTop
        if (edge === "bottom") top = maxBottom - box.height
        if (edge === "middle") top = centerY - box.height / 2
        left = Math.min(Math.max(left, 0), bounds.width)
        top = Math.min(Math.max(top, 0), bounds.height)
        nextPositionById.set(box.id, {left, top})
    }

    return fields.map((field) => {
        const next = nextPositionById.get(field.id)
        if (!next) return field
        if (field.placeInformation.left === next.left && field.placeInformation.top === next.top) return field
        return {
            ...field,
            placeInformation: {...field.placeInformation, left: next.left, top: next.top}
        }
    })
}
